import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Save } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormLabel } from "@/components/ui/form";
import { StickyActionBar } from "@/components/layout/StickyActionBar";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { getInstituicaoRegistro } from "./formApi";
import { getConfiguracaoInstituicao, saveConfiguracaoInstituicao } from "./configuracaoApi";
import { configuracaoFormSchema, type ConfiguracaoFormValues } from "./configuracaoSchema";

// Configurações operacionais da instituição (entity ConfiguracaoInstituicao,
// 1:1 com Instituicao). Página própria — e não uma sanfona/modal dentro de
// FormInstituicao — porque tem endpoint e ciclo de salvamento independentes e
// vai receber campos hoje em Instituicao. Seções novas entram em SECOES.
const SECOES: { titulo: string; campos: { name: keyof ConfiguracaoFormValues; label: string }[] }[] = [
  {
    titulo: "Estoque",
    campos: [
      { name: "seCalcularDisponibilidadeEstoque", label: "Calcular disponibilidade de estoque" },
      { name: "seCalcularDisponibilidadeVencimentoFichaAtual", label: "Calcular disponibilidade considerando o vencimento da ficha atual" },
    ],
  },
  {
    titulo: "Evolução",
    campos: [{ name: "seBuscarDadosEvolucaoAnterior", label: "Buscar dados da evolução anterior" }],
  },
  {
    titulo: "SNCR",
    campos: [{ name: "seLiberarSncr", label: "Liberar SNCR" }],
  },
];

export default function FormConfiguracaoInstituicao() {
  const { idInstituicao } = useParams<{ idInstituicao: string }>();
  const id = Number(idInstituicao);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);

  const form = useForm<ConfiguracaoFormValues>({ resolver: zodResolver(configuracaoFormSchema) });

  const { data: instituicao } = useQuery({
    queryKey: ["instituicao", "registro", idInstituicao],
    queryFn: () => getInstituicaoRegistro(id),
  });
  const { data: configuracao, isLoading } = useQuery({
    queryKey: ["instituicao", "configuracao", idInstituicao],
    queryFn: () => getConfiguracaoInstituicao(id),
  });

  useEffect(() => {
    if (configuracao) form.reset(configuracao as ConfiguracaoFormValues);
  }, [configuracao, form]);

  const salvar = useMutation({
    mutationFn: (values: ConfiguracaoFormValues) => saveConfiguracaoInstituicao(id, { ...configuracao, ...values }),
    onSuccess: () => {
      toast.success("Configurações salvas.");
      qc.invalidateQueries({ queryKey: ["instituicao"] });
      navigate("/Instituicao/Consulta");
    },
  });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit((values) => salvar.mutate(values))} className="space-y-6 pb-20">
        <div>
          <h1 className="text-lg font-semibold text-foreground">Configurações da Instituição</h1>
          <p className="text-muted-foreground">{instituicao?.nomeFantasia ?? "…"}</p>
        </div>

        {SECOES.map((secao) => (
          <section key={secao.titulo} className="space-y-3">
            <h2 className="text-base font-semibold text-foreground">{secao.titulo}</h2>
            {secao.campos.map(({ name, label }) => (
              <FormField
                key={name}
                control={form.control}
                name={name}
                render={({ field }) => (
                  <FormItem className="flex items-center gap-2 space-y-0">
                    <FormControl>
                      <Checkbox
                        disabled={isLoading}
                        checked={field.value === "S"}
                        onCheckedChange={(c) => field.onChange(c ? "S" : "N")}
                      />
                    </FormControl>
                    <FormLabel className="!mt-0 font-normal">{label}</FormLabel>
                  </FormItem>
                )}
              />
            ))}
          </section>
        ))}

        <StickyActionBar>
          <Button type="submit" disabled={salvar.isPending || isLoading}>
            {salvar.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
            {salvar.isPending ? "Salvando..." : "Salvar"}
          </Button>
          <Button type="button" variant="outline" onClick={() => setConfirmCancelOpen(true)}>
            Cancelar
          </Button>
        </StickyActionBar>
      </form>

      <ConfirmDialog
        open={confirmCancelOpen}
        onOpenChange={setConfirmCancelOpen}
        title="Cancelar alterações?"
        description="As alterações não salvas serão perdidas."
        confirmLabel="Sair sem salvar"
        onConfirm={() => navigate("/Instituicao/Consulta")}
      />
    </Form>
  );
}
